/**
 * Keyboard-aware viewport plugin.
 *
 * Mirrors window.visualViewport into CSS custom properties so global modal
 * styles can size themselves to the area that is actually visible when the
 * on-screen keyboard is open (iOS does not shrink the layout viewport).
 *
 *   --vv-height  visible height in px
 *   --vv-top     visible offset from the top of the layout viewport in px
 *   html.kb-open present while the keyboard is judged to be open
 *
 * See docs/superpowers/specs/2026-09-11-modal-keyboard-sheet-design.md
 */

export const KEYBOARD_THRESHOLD_PX = 100;

/**
 * Pure: turn raw viewport numbers into what the DOM layer needs.
 * @param {{ innerHeight: number, vvHeight?: number, vvOffsetTop?: number }} raw
 * @returns {{ height: number, top: number, keyboardOpen: boolean }}
 */
export function getViewportMetrics({ innerHeight, vvHeight, vvOffsetTop }) {
  if (vvHeight === undefined || vvHeight === null) {
    return { height: Math.round(innerHeight), top: 0, keyboardOpen: false };
  }
  const height = Math.round(vvHeight);
  const top = Math.round(vvOffsetTop || 0);
  const keyboardOpen = innerHeight - vvHeight > KEYBOARD_THRESHOLD_PX;
  return { height, top, keyboardOpen };
}

export const FOCUS_SETTLE_MS = 300;

const FIELD_TAGS = /^(INPUT|SELECT|TEXTAREA)$/;

function isModalField(el) {
  return Boolean(
    el &&
      FIELD_TAGS.test(el.tagName || "") &&
      typeof el.closest === "function" &&
      el.closest(".modal-body")
  );
}

/**
 * Build the controller. Everything the browser normally supplies is injected
 * so the logic can be unit-tested with plain objects.
 */
export function createKeyboardViewport({
  win,
  doc,
  raf = (fn) => win.requestAnimationFrame(fn),
  setTimeoutFn = (fn, ms) => win.setTimeout(fn, ms),
  clearTimeoutFn = (id) => win.clearTimeout(id),
} = {}) {
  const root = doc.documentElement;
  let keyboardOpen = false;
  let frame = 0;
  let settleTimer = 0;
  let listeners = [];

  function readMetrics() {
    const vv = win.visualViewport;
    return getViewportMetrics({
      innerHeight: win.innerHeight,
      vvHeight: vv ? vv.height : undefined,
      vvOffsetTop: vv ? vv.offsetTop : undefined,
    });
  }

  function reveal(el) {
    if (!isModalField(el)) return;
    if (settleTimer) clearTimeoutFn(settleTimer);
    settleTimer = setTimeoutFn(() => {
      settleTimer = 0;
      if (!keyboardOpen || el.isConnected === false) return;
      try {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch (e) {
        el.scrollIntoView();
      }
    }, FOCUS_SETTLE_MS);
  }

  function apply() {
    frame = 0;
    const m = readMetrics();
    root.style.setProperty("--vv-height", `${m.height}px`);
    root.style.setProperty("--vv-top", `${m.top}px`);
    const wasOpen = keyboardOpen;
    keyboardOpen = m.keyboardOpen;
    root.classList.toggle("kb-open", keyboardOpen);
    if (keyboardOpen && !wasOpen) reveal(doc.activeElement);
  }

  function update() {
    if (frame) return;
    // Mark pending before scheduling: apply() clears it, and a synchronous
    // raf (tests) would otherwise run apply() before the flag is set.
    frame = 1;
    raf(apply);
  }

  function onFocusIn(event) {
    if (!keyboardOpen) return;
    reveal(event.target);
  }

  function listen(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    listeners.push(() => target.removeEventListener(type, fn, opts));
  }

  return {
    start() {
      const vv = win.visualViewport;
      if (vv) {
        listen(vv, "resize", update);
        listen(vv, "scroll", update);
      }
      listen(win, "resize", update);
      listen(doc, "focusin", onFocusIn, true);
      apply();
    },
    stop() {
      listeners.forEach((off) => off());
      listeners = [];
      if (settleTimer) clearTimeoutFn(settleTimer);
      settleTimer = 0;
    },
    update,
  };
}

export default {
  install() {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    createKeyboardViewport({ win: window, doc: document }).start();
  },
};
